import { Editor as DraftEditor, EditorState, RichUtils, getDefaultKeyBinding } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { ButtonIcon, Flex } from 'components';
import { ActionButton, EditorStyle, Placeholder, SubmitButton } from './styles';
import useComments from 'features/comments/useComments';
import { CommentsTypeEnum } from 'features/comments/types';
import { useTheme } from 'styled-components';

var INLINE_STYLES = [
  { label: 'Negrito', style: 'BOLD' },
  { label: 'ITalico', style: 'ITALIC' },
  { label: 'Underline', style: 'UNDERLINE' },
];

interface FormProps {
  commentUuid?: string;
  commentType?: CommentsTypeEnum;
  collapsed?: boolean;
}

const Form = ({ commentUuid, commentType = CommentsTypeEnum.Comment, collapsed: receivedCollapsed = true }: FormProps) => {
  const { currentTheme } = useTheme();

  const [collapsed, setCollapsed] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const { createComment, setReplyingCommentUuid } = useComments();

  const editor = useRef<DraftEditor>(null);
  var currentStyle = editorState.getCurrentInlineStyle();

  const handleChangeEditor = (editorState: EditorState) => setEditorState(editorState);

  const handleClearEditor = useCallback(() => {
    setEditorState(EditorState.createEmpty());
  }, []);

  const handleCloseEditor = useCallback(() => {
    setCollapsed(false);
    handleClearEditor();
    setReplyingCommentUuid('');
    editor?.current?.blur();
  }, [editor, editorState]);

  const handleFocusEditor = useCallback(() => {
    setCollapsed(true);
    editor?.current?.focus();
  }, [editor]);

  const handleStyleButtonToggle = useCallback(
    (style: any) => {
      const nextState = RichUtils.toggleInlineStyle(editorState, style);
      setEditorState(nextState);
    },
    [editorState]
  );

  const handleSubmit = useCallback(async () => {
    const commentHtml = convertToHTML(editorState.getCurrentContent());

    await createComment(
      {
        commentType,
        text: commentHtml,
      },
      commentUuid
    );

    // handleClearEditor();
    // handleFocusEditor();
    handleCloseEditor();
  }, [editorState, commentUuid, commentType]);

  const onFocusEditor = useCallback(() => {
    setCollapsed(true);
  }, []);

  const myKeyBindingFn = useCallback(
    (e: any) => {
      if (e.key === 'Enter' && e.metaKey) {
        e.preventDefault();
        handleSubmit();
      }

      if (e.key === 'Escape') {
        setTimeout(handleCloseEditor, 10);
      }

      return getDefaultKeyBinding(e);
    },
    [editorState, commentUuid, commentType]
  );

  useEffect(() => {
    if (receivedCollapsed) {
      handleFocusEditor();
    }
  }, [receivedCollapsed]);

  return (
    <Flex className={clsx('comments-editor', { collapsed: collapsed })} flexDirection="column">
      <Flex flexGrow={1} flexDirection="column" alignItems="stretch" position="relative">
        {collapsed ? (
          <ButtonIcon
            position="absolute"
            bg="transparent"
            color="primary_400"
            colorHover="primary_200"
            top={2}
            right={2}
            type="button"
            icon="close"
            onClick={handleCloseEditor}
            iconWidth={22}
          />
        ) : (
          <Placeholder onClick={handleFocusEditor}>Adicione seu comentário ou pergunta</Placeholder>
        )}

        <DraftEditor
          ref={editor}
          editorState={editorState}
          keyBindingFn={myKeyBindingFn}
          // onEscape={handleCloseEditor}
          onFocus={onFocusEditor}
          onChange={handleChangeEditor}
        />
      </Flex>

      {collapsed && (
        <>
          <Flex marginTop="1px" height={{ m: 40 }}>
            <Flex flexGrow={1} marginRight="1px" alignItems="center" bg="commentsFormBg" borderBottomLeftRadius={4}>
              {INLINE_STYLES.map((type) => {
                const active = currentStyle.has(type.style);

                return (
                  <ActionButton
                    key={type.label}
                    data-active={active}
                    data-icon={type.style.toLocaleLowerCase()}
                    data-theme={currentTheme}
                    onClick={() => handleStyleButtonToggle(type.style)}
                  />
                );
              })}
            </Flex>

            <SubmitButton onClick={handleSubmit} type="button">
              Comentar
            </SubmitButton>
          </Flex>
        </>
      )}

      <EditorStyle />
    </Flex>
  );
};

export default Form;
